import { useState, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import {
  Text,
  Title,
  Group,
  Badge,
  Button,
  Image,
  Stack,
  Divider,
  Spoiler,
  Box,
  Flex,
  Avatar,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconMapPin,
  IconClock,
  IconTicket,
  IconExternalLink,
  IconNavigationPin,
  IconChevronRight,
} from '@tabler/icons-react';

import { ArtistCard } from '../../components/ui';

import '../../styles/ShowView.css';

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  event_type_ids: number[];
  event_types: { id: number; name: string }[];
  dance_style_ids: number[];
  dance_styles: { id: number; name: string }[];
  artist_ids: number[];
  artists: { id: number; name: string; avatar_url?: string }[];
  address: string;
  city: string;
  state: string;
  country?: string;
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  [key: string]: any;
  area?: {
    name: string;
    slug: string;
    strip_area_suffix: boolean;
    country_id: number;
    featured: boolean;
    events_count: number;
  };
}

interface EventDrawerContentProps {
  event: Event;
  data?: Event;
}

export default function EventDrawerContent({ event }: EventDrawerContentProps) {
  useEffect(() => {
    console.log('Event data:', event);
    console.log('Event types:', event.event_types);
    console.log('Dance styles:', event.dance_styles);
    console.log('Artists:', event.artists);
  }, [event]);

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC', // Forces display in UTC time
    });
  };

  // Format date range
  const formatDateRange = () => {
    const startDate = new Date(event.event_start_date);
    const endDate = new Date(event.event_end_date);
    if (startDate.toDateString() === endDate.toDateString()) {
      return formatDate(event.event_start_date);
    }
    return `${formatDate(event.event_start_date)} - ${formatDate(event.event_end_date)}`;
  };

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);

  // Determine if the event is in the past
  const isPastEvent = new Date(event.event_end_date) < yesterday;
  const isDiscarded = !!event.discarded_at;

  // Format month for the date box
  const getMonthAbbreviation = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  };

  // Format day for the date box
  const getDayOfMonth = (dateString: string) => {
    const date = new Date(dateString);
    return date.getDate();
  };

  // Get day of week and full date
  const getFullDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const areSameDates = (startDateStr: string, endDateStr: string) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(endDateStr);
    return (
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate()
    );
  };

  const month = getMonthAbbreviation(event.event_start_date);
  const day = getDayOfMonth(event.event_start_date);
  const fullStartDate = getFullDisplayDate(event.event_start_date);
  const fullEndDate = getFullDisplayDate(event.event_end_date);
  const startTime = formatTime(event.event_start_date);
  const endTime = formatTime(event.event_end_date);

  console.log('Start Time:', startTime);
  console.log('End Time:', endTime);

  // Get country flag emoji
  const getCountryFlag = (country?: string) => {
    if (!country || country.toLowerCase() === 'united states' || country.toLowerCase() === 'usa') {
      return '🇺🇸';
    }
    return '';
  };

  const areaName = event.area
    ? event.area.strip_area_suffix
      ? // remove trailing “Area” (with optional preceding space), case‑insensitive
        event.area.name.replace(/\s*\bArea\b\s*$/i, '')
      : event.area.name
    : '';
  console.log('Area Name:', areaName);

  const areaContent = event?.area ? (
    <Link href={`/city/${event?.area?.slug}`}>
      <Button
        component="button" // Changed from "a" to "button" since it's now inside a Link
        variant="filled"
        color="#EA580C"
        radius="md"
        size="xs"
        styles={{
          root: {
            padding: '4px 8px',
            display: 'inline-flex',
            alignItems: 'center',
          },
          leftIcon: { marginRight: 4 },
          rightIcon: { marginLeft: 4 },
          label: { fontSize: '0.875rem', fontWeight: 500 },
        }}
        leftSection={<IconMapPin size={14} />}
        rightSection={<IconChevronRight size={14} />}
      >
        Listed in {areaName}
      </Button>
    </Link>
  ) : null;

  return (
    <Stack gap="md" color="gray">
      {areaContent}
      {/* Event Image */}
      {event.banner_url && (
        <div
          style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}
        >
          <Image
            src={event.banner_url}
            alt={event.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '8px',
              marginBottom: '0px',
            }}
          />
        </div>
      )}

      {/* Event Title */}
      <Group>
        <Title order={2} className="poppins" style={{ color: '#f2f2f2', lineHeight: 1, marginBottom: 0 }}>
          {event.name}
        </Title>
      </Group>

      <div style={{ marginBottom: '-4px' }}>
        <Text size="lg" fw={600} style={{ color: '#f2f2f2', lineHeight: 1 }}>
          About Event
        </Text>
      </div>
      <Divider color="#757575" mt={0} mb={0} />

      <Flex mih={50} gap="0" justify="flex-start" align="flex-start" direction="column" wrap="wrap">
        <Flex align="center" gap="md" mb="0" mt="0">
          {/* Date Box */}
          <Box
            style={{
              backgroundColor: '#222',
              borderRadius: '8px',
              width: '48px',
              height: '48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0',
              margin: '0',
              color: '#fff',
              flexShrink: 0,
              overflow: 'hidden',
              border: '1px solid #75757530',
            }}
          >
            <Box style={{ backgroundColor: '#ea580c50', width: '100%', textAlign: 'center', padding: '1px 0px' }}>
              <Text size="sm" fw={600} style={{ lineHeight: 1.1, color: '#fff' }}>
                {month}
              </Text>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, width: '100%' }}>
              <Text size="xl" fw={700} style={{ lineHeight: 0 }}>
                {day}
              </Text>
            </Box>
          </Box>

          {/* Date and Time Text */}
          <div>
            <Text size="md" fw={600} style={{ color: '#f2f2f2', margin: '0px', padding: '0px', lineHeight: 1 }}>
              {areSameDates(event.event_start_date, event.event_end_date)
                ? fullStartDate
                : `${fullStartDate} - ${fullEndDate}`}
            </Text>
            <Text size="sm" style={{ color: '#ccc', marginTop: '8px', lineHeight: 1 }}>
              {startTime}-{endTime}
            </Text>
          </div>
        </Flex>

        {/* Merged Address Section with Navigation Icon */}
        <Flex align="center" gap="10px" mt="xs">
          <Box
            style={{
              backgroundColor: '#222',
              borderRadius: '8px',
              width: '48px',
              height: '48px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '0px',
              color: '#fff',
              border: '1px solid #75757530',
              flexShrink: 0,
            }}
          >
            <IconMapPin size={28} color="#ea580c" />
          </Box>
          <Text
            size="md"
            fw={600}
            sx={(theme) => ({
              width: '100%', // full width on mobile/smaller screens
              [theme.fn.largerThan('sm')]: { width: 400 }, // 400px on larger screens
            })}
            style={{ color: '#f2f2f2', lineHeight: 1 }}
          >
            {event.address}
          </Text>
          <Tooltip label="Open in Google Maps">
            <ActionIcon
              component="a"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`}
              target="_blank"
              variant="transparent"
            >
              <IconNavigationPin size={28} color="#ea580c" />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Flex>

      {/* Description */}
      <Box mb="xl">
        <div style={{ marginBottom: '4px' }}>
          <Text size="lg" fw={600}>
            Details{' '}
          </Text>
        </div>
        <Divider color="#757575" mt={0} mb={6} />
        <Spoiler maxHeight={150} mt="0" showLabel="Read more" hideLabel="Show less" color="orange">
          <Text
            mt="xs"
            dangerouslySetInnerHTML={{ __html: event.description }}
            style={{ color: '#FFF7ED' }}
            className="event-description"
          />
        </Spoiler>
      </Box>

      {/* Event Types */}
      {event.event_types && event.event_types.length > 0 && (
        <>
          <div style={{ marginBottom: '-12px', marginTop: '16px' }}>
            <Text size="lg" fw={600}>
              Event Tags
            </Text>
          </div>
          <Divider color="#757575" mt={0} mb={6} />
          <Group gap="xs">
            {event.event_types.map((type) => (
              <Badge
                key={type.id}
                size="lg"
                color="#EA580C"
                variant="filled"
                className="poppins"
                style={{ textTransform: 'none' }}
              >
                {type.name}
              </Badge>
            ))}
          </Group>
        </>
      )}

      {/* Dance Styles */}
      {event.dance_styles && event.dance_styles.length > 0 && (
        <>
          <div style={{ marginBottom: '-12px', marginTop: '16px' }}>
            <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
              Dance Styles
            </Text>
          </div>
          <Divider color="#757575" mt={0} mb={0} />
          <Group gap="xs" mb={16}>
            {event.dance_styles.map((style: { id: number; name: string }) => (
              <Badge
                key={style.id}
                color="#EA580C"
                size="lg"
                variant="filled"
                style={{ backgroundColor: '#EA580C', textTransform: 'none' }}
                className="poppins"
              >
                {style.name}
              </Badge>
            ))}
          </Group>
        </>
      )}

      {/* Artists Section */}
      {event.artists && event.artists.length > 0 && (
        <>
          <div style={{ marginBottom: '-20px', marginTop: '6px' }}>
            <Text size="lg" fw={600} style={{ color: '#f2f2f2' }}>
              Artists
            </Text>
          </div>
          <Divider color="#757575" mt={0} mb={16} />
          <Group gap="xs" mb={16} align="flex-start">
            <Group gap="sm" wrap="wrap" className="artist-grid">
              {event.artists.map((artist) => (
                <ArtistCard key={artist.id} artist={artist} />
              ))}
            </Group>
          </Group>
        </>
      )}

      {/* Uncomment Action Buttons if needed */}
      {/*
      <Group grow mt="xl">
        <Button component="a" href={`/events/${event.slug}`} variant="filled" color="#EA580C" leftSection={<IconExternalLink size={16} />}>
          View Full Event Page
        </Button>
        {!isPastEvent && event.ticket_url && (
          <Button component="a" href={event.ticket_url} variant="outline" color="#EA580C" leftSection={<IconTicket size={16} />} target="_blank">
            Get Tickets
          </Button>
        )}
      </Group>
      */}
    </Stack>
  );
}
